import { ReactNode } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { Link } from '@tanstack/react-router';

import { ChessBoardSkeleton } from '@exports/ChessBoard';
import { AnimatedNode } from '@ui/components/Animation';
import { Loader } from '@ui/components/Loader';
import { SlimArrowButton } from '@ui/components/SlimArrowButton';
import { FeatherIcon } from '@ui/icons/FeatherIcon';

import styles from './GameLayout.module.css';

export type Props = {
  board: ReactNode;
  avatar?: ReactNode;
  header?: ReactNode;
  popup?: ReactNode;
  errorToast?: ReactNode;
  player1: ReactNode;
  player2: ReactNode;
  scoresheet: ReactNode;
  actions?: ReactNode;
  backLink?: string;
  backLinkIsLogo?: boolean;
  onBackClick?: () => void;
};

export function GameLayout(props: Props) {
  const device = useMediaQuery();
  return device === 'desktop' ? <DesktopLayout {...props} /> : <MobileLayout {...props} />;
}

export function GameLayoutSkeleton() {
  const device = useMediaQuery();
  return device === 'desktop' ? (
    <DesktopLayoutSkeleton />
  ) : (
    <MobileLayoutSkeleton isTablet={device === 'tablet'} />
  );
}

/*
    ____,-------------------------------,____
    \   |             Layouts           |   /
    /___|-------------------------------|___\
*/

function DesktopLayout({
  board,
  avatar,
  header,
  popup,
  errorToast,
  player1,
  player2,
  scoresheet,
  actions,
  backLink,
  backLinkIsLogo,
  onBackClick,
}: Props) {
  return (
    <div className={styles.layout} translate="no">
      {avatar && <div className={styles.avatar}>{avatar}</div>}
      <div className={styles.board}>
        {(backLink || onBackClick) && (
          <Link
            to={backLink}
            onClick={
              onBackClick
                ? (e) => {
                    e.preventDefault();
                    onBackClick();
                  }
                : undefined
            }
            className={styles.desktopBackBtn}
          >
            {backLinkIsLogo ? (
              <FeatherIcon className={styles.logo} />
            ) : (
              <SlimArrowButton direction="left" size="24" colorPreset="currentColor" />
            )}
          </Link>
        )}
        {board}
      </div>
      <div className={styles.sidebar}>
        <div className={styles.header}>
          {header}
          <div className={styles.popup}>
            <AnimatedNode className={styles.popupInner}>{popup}</AnimatedNode>
          </div>
        </div>
        <div className={styles.game}>
          {player1}
          <div className={styles.scoresheet}>{scoresheet}</div>
          {player2}
        </div>
        <AnimatedNode persist className={styles.footer}>
          {actions}
        </AnimatedNode>
        <AnimatedNode animation="bounce-up" className={styles.toast}>
          {errorToast}
        </AnimatedNode>
      </div>
    </div>
  );
}

function MobileLayout({
  board,
  avatar,
  header,
  popup,
  errorToast,
  player1,
  player2,
  scoresheet,
  actions,
  backLink: _backlink,
  backLinkIsLogo: _backLinkIsLogo,
  onBackClick: _onBackClick,
}: Props) {
  return (
    <div className={styles.layoutMobile}>
      {avatar && <div className={styles.avatar}>{avatar}</div>}
      {header}
      {player1}
      <div className={styles.board}>{board}</div>
      {player2}
      <div className={styles.actionsMobileWrapper}>
        {actions}
        <AnimatedNode className={styles.mobilePopup}>{popup}</AnimatedNode>
        <AnimatedNode className={styles.mobileToast}>{errorToast}</AnimatedNode>
      </div>
      <div className={styles.scoresheetMobile}>{scoresheet}</div>
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |           Skeletons           |   /
    /___|-------------------------------|___\
*/

function DesktopLayoutSkeleton() {
  return (
    <div className={styles.layout}>
      <div className={styles.board}>
        {
          <ChessBoardSkeleton>
            <Loader ignoreClubColors />
          </ChessBoardSkeleton>
        }
      </div>
      <div className={styles.sidebar}>
        <div className={styles.header}>
          <div className={styles.headerSkeletons} />
        </div>
      </div>
    </div>
  );
}

function MobileLayoutSkeleton({ isTablet }: { isTablet: boolean }) {
  return (
    <div className={styles.layoutMobile}>
      <div className={styles.headerSkeletons} />
      <div style={{ height: 32 }} />
      <div className={styles.board}>
        <ChessBoardSkeleton>
          <Loader preset={isTablet ? 'desktop' : 'mobile'} ignoreClubColors />
        </ChessBoardSkeleton>
      </div>
      <div className={styles.actionsMobile} />
      <div className={styles.scoresheetMobile} />
    </div>
  );
}
